import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

const ProductDetailContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: #ffffff;
  min-height: 100vh;
`;

const BackButton = styled(motion.button)`
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: #b91c1c;
    transform: translateY(-2px);
  }
`;

const ProductHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: contain;
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  line-height: 1.2;
`;

const ProductProvider = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border-radius: 8px;
  width: fit-content;
  font-weight: 600;
  color: #374151;
`;

const ProductDescription = styled.div`
  background: #f8fafc;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const DescriptionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::before {
    content: '';
    width: 4px;
    height: 25px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 2px;
  }
`;

const DescriptionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DescriptionItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  font-weight: 500;
  color: #374151;
  line-height: 1.5;

  &::before {
    content: '✓';
    color: #dc2626;
    font-weight: 700;
    font-size: 1.1rem;
    margin-top: 0.1rem;
  }
`;

const TechnicalSpecsSection = styled.div`
  background: #f8fafc;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::before {
    content: '';
    width: 4px;
    height: 30px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 2px;
  }
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const SpecItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SpecLabel = styled.span`
  font-weight: 600;
  color: #374151;
`;

const SpecValue = styled.span`
  font-weight: 500;
  color: #1f2937;
`;


const ContactSection = styled.div`
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  padding: 3rem;
  border-radius: 16px;
  text-align: center;
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
`;

const ContactInfo = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
`;

const LoadingText = styled.p`
  font-size: 1.2rem;
  color: #64748b;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  text-align: center;
`;

const ErrorText = styled.p`
  font-size: 1.2rem;
  color: #dc2626;
  margin-bottom: 2rem;
`;

const ProductDetail: React.FC = () => {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setError('Product ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('ProductDetail: Fetching product with ID:', productId);
        const productData = await FirebaseService.getProduct(productId);
        console.log('ProductDetail: Product data received:', productData);
        
        if (productData) {
          setProduct(productData);
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('ProductDetail: Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <ProductDetailContainer>
        <LoadingContainer>
          <LoadingText>Loading product details...</LoadingText>
        </LoadingContainer>
      </ProductDetailContainer>
    );
  }

  if (error) {
    return (
      <ProductDetailContainer>
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
          <BackButton onClick={() => navigate(-1)}>
            ← Go Back
          </BackButton>
        </ErrorContainer>
      </ProductDetailContainer>
    );
  }

  if (!product) {
    return (
      <ProductDetailContainer>
        <ErrorContainer>
          <ErrorText>Product not found</ErrorText>
          <BackButton onClick={() => navigate(-1)}>
            ← Go Back
          </BackButton>
        </ErrorContainer>
      </ProductDetailContainer>
    );
  }

  return (
    <ProductDetailContainer>
      <BackButton
        onClick={() => navigate(-1)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        ← Back
      </BackButton>

      <ProductHeader>
        <ProductImage
          src={product.mainImage || product.image || '/images/logo.png'}
          alt={product.name || product.title || 'Product Image'}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/images/logo.png';
          }}
        />
        <ProductInfo>
          <ProductTitle>{product.name || product.title || 'Product'}</ProductTitle>
          <ProductProvider>
            <span>���</span>
            {product.provider || product.brand || 'TECHYID'}
          </ProductProvider>
        </ProductInfo>
      </ProductHeader>

      <ProductDescription>
        <DescriptionTitle>Description du Produit</DescriptionTitle>
        <DescriptionList>
          {product.description && product.description.length > 0 ? (
            product.description.map((desc, index) => (
              <DescriptionItem key={index}>{desc}</DescriptionItem>
            ))
          ) : (
            <DescriptionItem>Description non disponible</DescriptionItem>
          )}
        </DescriptionList>
      </ProductDescription>

      {product.technicalSpecs && Object.keys(product.technicalSpecs).length > 0 && (
        <TechnicalSpecsSection>
          <SectionTitle>Spécifications Techniques</SectionTitle>
          <SpecsGrid>
            {Object.entries(product.technicalSpecs).map(([key, value]) => (
              <SpecItem key={key}>
                <SpecLabel>{key}</SpecLabel>
                <SpecValue>{String(value)}</SpecValue>
              </SpecItem>
            ))}
          </SpecsGrid>
        </TechnicalSpecsSection>
      )}

      <ContactSection>
        <ContactTitle>Besoin d'informations ?</ContactTitle>
        <ContactInfo>
          <ContactItem href="tel:+212522123456">
            <span>���</span>
            +212 522 123 456
          </ContactItem>
          <ContactItem href="mailto:info@techyid.ma">
            <span>✉️</span>
            info@techyid.ma
          </ContactItem>
        </ContactInfo>
      </ContactSection>
    </ProductDetailContainer>
  );
};

export default ProductDetail;
